import { useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fieldErrorsFrom422, formatApiErrorDetail, formatMoney } from "@/lib/api";
import { FormField, adminInput } from "./shared";

const LINE = { description: "", quantity: "1", rate: "" };
const BLANK = { project_id: "", type: "Invoice", due_date: "", line_items: [LINE] };

const validate = (f) => {
  const e = {};
  if (!f.project_id) e.project_id = "Please select a project";
  if (!f.line_items.length) e.line_items = "Add at least one line item";
  f.line_items.forEach((li, i) => {
    const q = Number(li.quantity), r = Number(li.rate);
    if (!li.description.trim() || !Number.isInteger(q) || q < 1 || Number.isNaN(r) || r <= 0 || li.rate === "")
      e.line_items = `Line ${i + 1}: enter a description, a whole quantity of at least 1 and a positive rate`;
  });
  return e;
};

export const LineRow = ({ li, i, onChange, onRemove }) => (
  <div className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[1fr_70px_100px_auto]">
    <input placeholder="Description" value={li.description} onChange={(e) => onChange(i, "description", e.target.value)} data-testid={`request-line-description-${i}`} className={`${adminInput(false)} col-span-2 sm:col-span-1`} />
    <input type="number" min="1" step="1" placeholder="Qty" value={li.quantity} onChange={(e) => onChange(i, "quantity", e.target.value)} data-testid={`request-line-quantity-${i}`} className={adminInput(false)} />
    <input type="number" min="0" step="0.01" placeholder="Rate" value={li.rate} onChange={(e) => onChange(i, "rate", e.target.value)} data-testid={`request-line-rate-${i}`} className={adminInput(false)} />
    <button type="button" onClick={() => onRemove(i)} aria-label="Remove line" data-testid={`request-line-remove-${i}`} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-red-300"><X className="h-4 w-4" /></button>
  </div>
);

export const PaymentRequestDialog = ({ open, onClose, onSave }) => {
  const [form, setForm] = useState(BLANK);
  const [projects, setProjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(BLANK); setErrors({});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, [open]);

  const setLine = (i, k, v) => setForm({ ...form, line_items: form.line_items.map((li, j) => (j === i ? { ...li, [k]: v } : li)) });
  const total = form.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.rate) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await onSave({ project_id: form.project_id, type: form.type, due_date: form.due_date || null,
        line_items: form.line_items.map((li) => ({ description: li.description.trim(), quantity: Number(li.quantity), rate: Number(li.rate) })) });
    } catch (err) {
      const fe = fieldErrorsFrom422(err.response?.data?.detail);
      setErrors(Object.keys(fe).length ? { line_items: Object.values(fe).join(" ") , ...(fe.project_id ? { project_id: fe.project_id } : {}) } : { form: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-request-dialog" className="max-h-[90vh] max-w-xl overflow-y-auto border-white/10 bg-[#0D111A]">
        <DialogHeader><DialogTitle className="text-white">New payment request</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <FormField id="request-project_id" label="Client project" error={errors.project_id}>
            <select id="request-project_id" data-testid="request-input-project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={adminInput(errors.project_id)}>
              <option value="">Select a project</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.client_name}</option>)}
            </select>
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="request-type" label="Type">
              <select id="request-type" data-testid="request-input-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={adminInput(false)}>
                <option>Invoice</option><option>Deposit</option>
              </select>
            </FormField>
            <FormField id="request-due" label="Due date">
              <input id="request-due" type="date" data-testid="request-input-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={adminInput(false)} />
            </FormField>
          </div>
          <div>
            <p className="mb-1.5 text-sm text-slate-300">Line items</p>
            <div className="space-y-2">{form.line_items.map((li, i) => <LineRow key={i} li={li} i={i} onChange={setLine} onRemove={(j) => setForm({ ...form, line_items: form.line_items.filter((_, k) => k !== j) })} />)}</div>
            <button type="button" onClick={() => setForm({ ...form, line_items: [...form.line_items, LINE] })} data-testid="request-add-line-button" className="mt-2 inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-white"><Plus className="h-3.5 w-3.5" /> Add line</button>
            {errors.line_items && <p data-testid="request-line-items-error" className="mt-1 text-xs text-red-400">{errors.line_items}</p>}
          </div>
          <p className="text-right text-sm text-slate-400">Total <span data-testid="request-total-preview" className="ml-2 font-display text-xl font-bold text-white">{formatMoney(total)}</span></p>
          {errors.form && <p className="text-sm text-red-400">{errors.form}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} data-testid="request-cancel-button" className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="request-save-button" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create request</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const RetainerDialog = ({ open, onClose, onSave }) => {
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ client_id: "", package_id: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ client_id: "", package_id: "" }); setError("");
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
    api.get("/admin/packages").then((r) => setPlans(r.data.filter((p) => p.billing === "monthly"))).catch(() => setPlans([]));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.package_id) { setError("Select a client and a monthly plan"); return; }
    setBusy(true);
    try { await onSave(form); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="retainer-dialog" className="max-w-md border-white/10 bg-[#0D111A]">
        <DialogHeader><DialogTitle className="text-white">Assign monthly plan</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <FormField id="retainer-client" label="Client">
            <select id="retainer-client" data-testid="retainer-input-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={adminInput(false)}>
              <option value="">Select a client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.email}</option>)}
            </select>
          </FormField>
          <FormField id="retainer-plan" label="Monthly plan">
            <select id="retainer-plan" data-testid="retainer-input-package" value={form.package_id} onChange={(e) => setForm({ ...form, package_id: e.target.value })} className={adminInput(false)}>
              <option value="">Select a plan</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {formatMoney(p.price)}/month</option>)}
            </select>
          </FormField>
          {error && <p data-testid="retainer-error" className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-full border border-white/10 px-5 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
            <button type="submit" disabled={busy} data-testid="retainer-save-button" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Assign</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
